import { inject, Injectable } from '@angular/core';
import type { WidgetDataSubTypes } from '@common/enums';
import { WidgetsDataTypes } from '@common/enums';
import { Actions, ofType } from '@ngrx/effects';
import { FmFeatureApiService, type SelectedFileInterface } from '@sendpulse/file-manager';
import { UnsplashComponent } from '@sites/images';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { SiteActions } from '@web/store/website/site/site.actions';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, filter, shareReplay, take } from 'rxjs';
import type { FileEditType } from '@common/types/editor-file.type';
import type { NormalizedBlockEntity, NormalizedColumnEntity } from '@web/types';

import { backgroundImageOf } from '@web/core/services/entity-background-image';
import { environment } from '@web-env/environment';

type UploadImageParams = {
    index?: number;
    widgetId?: string;
    dataType: WidgetsDataTypes;
    dataSubType?: WidgetDataSubTypes;
    entity?: NormalizedBlockEntity | NormalizedColumnEntity;
    device?: 'desktop' | 'tablet' | 'mobile';
    isSync?: boolean;
};

@Injectable({ providedIn: 'root' })
export class UseUnsplash {
    private readonly modalService: BsModalService = inject(BsModalService);
    private readonly fmFeatureApiService = inject(FmFeatureApiService);
    private readonly siteFacade: SiteFacade = inject(SiteFacade);
    private readonly actions$: Actions = inject(Actions);

    public openUnsplashModal({
        index,
        widgetId,
        dataType,
        dataSubType,
        device,
        isSync,
        entity,
    }: UploadImageParams): void {
        const modalRef = this.modalService.show(UnsplashComponent, {
            class: 'modal-lg',
            backdrop: 'static',
            keyboard: false,
            initialState: {
                apiUrl: environment.unsplashApiUrl,
            },
        });

        this.actions$.pipe(ofType(SiteActions.uploadImageSuccess), take(1)).subscribe(() => {
            modalRef.hide();
        });

        combineLatest([
            modalRef.content.onConfirm,
            this.fmFeatureApiService.cdnInfo$.pipe(filter(Boolean), shareReplay(1)),
        ])
            .pipe(take(1))
            .subscribe(([image, cdnInfo]) => {
                if (!image) return;

                const payload: FileEditType = {
                    image,
                    widgetId,
                    dataType,
                    dataSubType,
                    index,
                    userHash: cdnInfo.userHash,
                    needResize: true,
                };

                if (dataType === WidgetsDataTypes.backgroundImage) {
                    payload.entityId = entity.id;
                    payload.entityImage = backgroundImageOf(entity, device);
                    payload.device = device;
                    payload.isSync = isSync;
                }

                this.siteFacade.uploadImage(payload);
            });
    }
}
